/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { Route, Switch, Redirect, matchPath } from 'react-router-dom';
import { translate, type TFunction } from 'react-i18next';
import { compose } from 'recompose';

import { clientSelector as clientIdSelector } from 'common/redux/selectors/queryParamsSelectors';
import { sessionSelector } from 'common/redux/selectors/sessionSelector';
import { clientSelector } from 'common/redux/selectors/clientSelector';

import { SidebarWithLink } from 'common/components/core/sidebar';
import {
    withRouter,
    type WithRouterProps,
    RouteNotFound
} from 'common/components/core/router';

import { Client } from 'common/models/internal/Client';
import { Session } from 'common/models/internal/Session';
import Products from 'common/enums/Product';
import withRouteTracker from 'common/middleware/RouteTracker';
import ClientConfigService from 'common/services/ClientConfigService';
import {
    withRoutePermissionCheck,
    RoleOperationEnum
} from 'common/modules/rbac';

import { lazy } from 'common/modules/mfe';

import LocationMasterContainer from 'common/entity/locationMaster/LocationMasterContainer';
import LineItemMasterContainer from 'common/entity/lineItemMaster/LineItemMasterContainer';
import ShiftMasterContainer from 'common/entity/shiftMaster/ShiftMasterContainer';
import OutletMasterContainer from 'common/entity/outletMaster/OutletMasterContainer';
import ZoneMapMasterContainer from 'common/entity/mapMaster/ZoneMapMasterContainer';
import RiderMasterContainer from 'common/entity/riderMaster/RiderMasterContainer';
import TransporterMasterContainer from 'common/entity/transporterMaster/TransporterMasterContainer';
import VehicleModelMasterContainer from 'common/entity/vehicleModelMaster/VehicleModelMasterContainer';
import ContractMasterContainer from 'common/entity/contractMaster/ContractMasterContainer';
import RiderPersonaMasterContainer from 'common/entity/riderPersonaMaster/RiderPersonaMasterContainer';
import VehicleMasterContainer from 'common/entity/vehicleMaster/VehicleMasterContainer';
import ContactMasterContainer from 'common/entity/contactMaster/ContactMasterContainer';
import TeamMasterContainer from 'common/entity/teamMaster/TeamMasterContainer';
import PersonnelMasterContainer from 'common/entity/personnelMaster/PersonnelMasterContainer';
import HomebaseMasterContainer from 'common/entity/homebaseMaster/HomebaseMasterContainer';
import OrderMasterContainer from 'common/entity/orderMaster/OrderMasterContainer';
import TerritoryMasterContainer from 'common/entity/territoryMaster/TerritoryMasterContainer';
import theme from './EntityManagementContainer.scss';

const BagModelMasterContainer = lazy(() =>
    import('orderiq-app/BagModelMasterContainer')
);

type OwnProps = {};

type Props = {
    ...OwnProps,
    selectedEntityId: string,
    clientId: string,
    client: ?Client,
    session: ?Session,
    t: TFunction,
    ...WithRouterProps
};

const EntityManagementContainer = ({
    client,
    session,
    t,
    clientId,
    location,
    match
}: Props) => {
    const sections = React.useMemo(() => {
        if (!client || !session) {
            return null;
        }
        const {
            entitySettings,
            sectionVisibilitySettings,
            orderManagementSettings
        } = client;
        const { personnel } = session;

        return [
            {
                id: 'masters',
                title: t('masterManagement'),
                nonSelectable: true,
                disabled: false,
                subsections: [
                    {
                        id: 'location',
                        path: `/client/${clientId}/entity/location`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterLocation'
                        }),
                        disabled: !entitySettings.locationSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                LocationMasterContainer,
                                RoleOperationEnum.GET_LOCATIONS
                            ),
                            Products.DISPATCHER
                        )
                    },
                    {
                        id: 'lineitem',
                        path: `/client/${clientId}/entity/lineitem`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterLineItem'
                        }),
                        disabled: !entitySettings.lineItemSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                LineItemMasterContainer,
                                RoleOperationEnum.GET_SKU
                            ),
                            Products.DISPATCHER
                        )
                    },
                    {
                        id: 'shift',
                        path: `/client/${clientId}/entity/shift`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterShift'
                        }),
                        disabled: !entitySettings.shiftSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                ShiftMasterContainer,
                                RoleOperationEnum.GET_SHIFT
                            ),
                            Products.DISPATCHER
                        )
                    },
                    {
                        id: 'transporter',
                        path: `/client/${clientId}/entity/transporter`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterTransporter'
                        }),
                        disabled: !entitySettings.transporterSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                TransporterMasterContainer,
                                RoleOperationEnum.GET_TRANSPORTER
                            ),
                            Products.DISPATCHER
                        )
                    },
                    {
                        id: 'vehicleModel',
                        path: `/client/${clientId}/entity/vehicleModel`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterVehicleModel'
                        }),
                        disabled: !entitySettings.vehicleModelSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                VehicleModelMasterContainer,
                                RoleOperationEnum.GET_VEHICLE_MODEL
                            ),
                            Products.DISPATCHER
                        )
                    },
                    {
                        id: 'vehicle',
                        path: `/client/${clientId}/entity/vehicle`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterVehicle'
                        }),
                        disabled: !entitySettings.vehicleSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                VehicleMasterContainer,
                                RoleOperationEnum.GET_VEHICLE
                            ),
                            Products.DISPATCHER
                        )
                    },
                    {
                        id: 'bagModel',
                        path: `/client/${clientId}/entity/bagModel`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterBagModel'
                        }),
                        disabled: !entitySettings.bagModelSettings.enabled,
                        Component: withRouteTracker(
                            BagModelMasterContainer,
                            Products.ORCHESTRATOR
                        )
                    },
                    {
                        id: 'riderPersona',
                        path: `/client/${clientId}/entity/riderPersona`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterRiderPersona'
                        }),
                        disabled: !entitySettings.riderPersonaSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                RiderPersonaMasterContainer,
                                RoleOperationEnum.GET_RIDER_PERSONA
                            ),
                            Products.MOTION_TRACK
                        )
                    },
                    {
                        id: 'rider',
                        path: `/client/${clientId}/entity/rider`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterRider'
                        }),
                        disabled: !entitySettings.riderSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                RiderMasterContainer,
                                RoleOperationEnum.GET_RIDER
                            ),
                            Products.MOTION_TRACK
                        )
                    },
                    {
                        id: 'contract',
                        path: `/client/${clientId}/entity/contract`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterContract'
                        }),
                        disabled: !entitySettings.contractSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                ContractMasterContainer,
                                RoleOperationEnum.GET_CONTRACT
                            ),
                            Products.DISPATCHER
                        )
                    },
                    {
                        id: 'contact',
                        path: `/client/${clientId}/entity/contact`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterContact'
                        }),
                        disabled: !entitySettings.contactSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                ContactMasterContainer,
                                RoleOperationEnum.GET_CONTACT
                            ),
                            Products.DISPATCHER
                        )
                    },
                    {
                        id: 'team',
                        path: `/client/${clientId}/entity/team`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterTeam'
                        }),
                        disabled: !(
                            entitySettings.teamSettings.enabled &&
                            (ClientConfigService.isRbacEnabled()
                                ? personnel.hasPermission(
                                      RoleOperationEnum.GET_TEAMS
                                  )
                                : personnel.isGlobalPersonnel() ||
                                  personnel.canManagePersonnel)
                        ),
                        Component: withRouteTracker(
                            TeamMasterContainer,
                            Products.PLATFORM
                        )
                    },
                    {
                        id: 'personnel',
                        path: `/client/${clientId}/entity/personnel`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterPersonnel'
                        }),
                        disabled: !(
                            entitySettings.personnelSettings.enabled &&
                            (ClientConfigService.isRbacEnabled()
                                ? personnel.hasPermission(
                                      RoleOperationEnum.GET_PERSONNELS
                                  )
                                : personnel.isGlobalPersonnel() ||
                                  personnel.canManagePersonnel)
                        ),
                        Component: withRouteTracker(
                            PersonnelMasterContainer,
                            Products.PLATFORM
                        )
                    },
                    {
                        id: 'homebase',
                        path: `/client/${clientId}/entity/homebase`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterHomebase'
                        }),
                        disabled: !entitySettings.homebaseSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                HomebaseMasterContainer,
                                RoleOperationEnum.GET_HOMEBASES
                            ),
                            Products.PLATFORM
                        )
                    },
                    {
                        id: 'order',
                        path: `/client/${clientId}/entity/order`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterOrder'
                        }),
                        disabled:
                            sectionVisibilitySettings.showOrderManagementSection ||
                            !orderManagementSettings.orderMasterSettings
                                .enabled,
                        Component: withRouteTracker(
                            OrderMasterContainer,
                            Products.ORCHESTRATOR
                        )
                    }
                ]
            },
            {
                id: 'pjpSection',
                title: t('entities:masterPjp_plural'),
                nonSelectable: true,
                disabled: false,
                subsections: [
                    {
                        id: 'outlet',
                        path: `/client/${clientId}/entity/outlet`,
                        title: t('entityMasterTitle', {
                            entityNameId: 'masterOutlet'
                        }),
                        disabled: !entitySettings.outletSettings.enabled,
                        Component: withRouteTracker(
                            withRoutePermissionCheck(
                                OutletMasterContainer,
                                RoleOperationEnum.GET_OUTLETS
                            ),
                            Products.FIELD_PRO
                        )
                    }
                ]
            },
            {
                id: 'map',
                path: `/client/${clientId}/entity/map`,
                title: t('entityMasterTitle', {
                    entityNameId: 'map'
                }),
                disabled: !entitySettings.mapSettings.enabled,
                Component: withRouteTracker(
                    ZoneMapMasterContainer,
                    Products.DISPATCHER
                ),
                subsections: null
            },
            {
                id: 'territory',
                path: `/client/${clientId}/entity/territory`,
                title: t('entityMasterTitle', {
                    entityNameId: 'masterTerritoryPlan'
                }),
                disabled: !sectionVisibilitySettings.showTerritorySection,
                Component: withRouteTracker(
                    TerritoryMasterContainer,
                    Products.FIELD_PRO
                )
            }
        ].map(section => ({
            ...section,
            disabled: section.subsections
                ? // disable section if all subsections disabled
                  section.subsections.every(subsection => subsection.disabled)
                : section.disabled
        }));
    }, [client, session, t, clientId]);

    const enabledRoutes = React.useMemo(() => {
        return (sections || []).reduce<
            $ReadOnlyArray<{
                +id: string,
                +path: string,
                +Component: React.AbstractComponent<mixed>
            }>
        >((acc, section) => {
            if (section.path && section.Component) {
                return [
                    ...acc,
                    {
                        id: section.id,
                        path: section.path,
                        Component: section.Component
                    }
                ];
            }

            if (section.subsections) {
                return [
                    ...acc,
                    ...section.subsections
                        .filter(subsection => !subsection.disabled)
                        .map(subsection => ({
                            id: subsection.id,
                            path: subsection.path,
                            Component: subsection.Component
                        }))
                ];
            }

            return acc;
        }, []);
    }, [sections]);

    const selectedEntityId = React.useMemo(() => {
        const customMatch = matchPath(location.pathname, {
            path: `${match.path}/:entityId`,
            exact: false,
            strict: false
        });
        return customMatch?.params.entityId;
    }, [location, match]);

    if (!client || !session || !sections) {
        return null;
    }

    return (
        <div className={theme.container}>
            <SidebarWithLink
                theme={theme}
                clientName={client.name}
                sections={sections}
                selectedSectionId={selectedEntityId}
            />
            <div className={theme.routesWrap}>
                <Switch>
                    {enabledRoutes.map(route => (
                        <Route
                            key={route.path}
                            path={`${match.path}/${route.id}`}
                            component={route.Component}
                        />
                    ))}
                    {enabledRoutes.length > 0 ? (
                        <Redirect
                            exact
                            from={match.path}
                            to={enabledRoutes[0].path}
                        />
                    ) : null}
                    <RouteNotFound />
                </Switch>
            </div>
        </div>
    );
};

export default (compose(
    withRouter,
    connect((state, ownProps) => {
        return {
            clientId: clientIdSelector(state, ownProps),
            session: sessionSelector(state),
            client: clientSelector(state, ownProps)
        };
    }),
    translate('entityManagement')
)(EntityManagementContainer): React.AbstractComponent<OwnProps>);
